import React from "react";
import { Typography, Chip, Box } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTranslation } from "react-i18next";
import { UserReadAll } from "src/store/api/generatedApi";
import { MenuItem } from "src/dashboard/menuItems/types";
import { getMenuItemPermissions } from "src/dashboard/menuItems/utils";

interface MenuItemStat {
  menuItem: MenuItem;
  userCount: number;
  users: UserReadAll[];
  isChild: boolean;
  parentPath?: string;
}

interface PermissionsTableProps {
  menuItemStats: MenuItemStat[];
  onMenuItemClick: (menuItem: MenuItem, users: UserReadAll[]) => void;
}

const MenuItemCell = ({ row }: GridRenderCellParams<MenuItemStat>) => {
  const { t } = useTranslation();
  const { menuItem, isChild } = row;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: "40px",
        pl: isChild ? 2 : 0,
      }}
    >
      {!isChild && menuItem.icon}
      <Typography
        variant="body2"
        fontWeight={isChild ? "normal" : "medium"}
        color={isChild ? "text.secondary" : "text.primary"}
      >
        {isChild ? "└ " : null}
        {t(menuItem.i18nKey)}
      </Typography>
    </Box>
  );
};

const PathCell = ({ row }: GridRenderCellParams<MenuItemStat>) => (
  <Typography variant="body2" color="text.secondary">
    {row.menuItem.path}
  </Typography>
);

const PermissionsCell = ({ row }: GridRenderCellParams<MenuItemStat>) => {
  const { t } = useTranslation();
  const { menuItem } = row;
  const permissions = getMenuItemPermissions(menuItem);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        py: 0.5,
      }}
    >
      {menuItem.requireAdmin ? (
        <Chip
          label={t("adminRequired")}
          size="small"
          color="error"
          variant="filled"
        />
      ) : null}
      {permissions.map((permission) => (
        <Chip
          key={permission}
          label={permission}
          size="small"
          variant="outlined"
        />
      ))}
      {!menuItem.requireAdmin && permissions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      ) : null}
    </Box>
  );
};

const FeatureFlagCell = ({ row }: GridRenderCellParams<MenuItemStat>) => {
  const { menuItem } = row;

  return menuItem.featureFlag ? (
    <Chip
      label={menuItem.featureFlag}
      size="small"
      color="info"
      variant="outlined"
    />
  ) : (
    <Typography variant="body2" color="text.secondary">
      -
    </Typography>
  );
};

const UserCountCell = ({ row }: GridRenderCellParams<MenuItemStat>) => (
  <Chip
    label={row.userCount}
    color={row.userCount > 0 ? "primary" : "default"}
    size="small"
  />
);

const UserNamesCell = ({ row }: GridRenderCellParams<MenuItemStat>) => {
  const { t } = useTranslation();
  const { userCount, users } = row;

  if (userCount <= 5 && userCount > 0) {
    return (
      <Typography variant="body2">
        {users.map((user) => user.name).join(", ")}
      </Typography>
    );
  }
  if (userCount > 5) {
    return (
      <Typography variant="body2" color="primary">
        {t("clickToSeeAllUsers")}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {t("noUsers")}
    </Typography>
  );
};

export const PermissionsTable: React.FC<PermissionsTableProps> = ({
  menuItemStats,
  onMenuItemClick,
}) => {
  const { t } = useTranslation();

  const columns: GridColDef<MenuItemStat>[] = [
    {
      field: "menuItem",
      headerName: t("menuItem"),
      flex: 20,
      display: "flex",
      sortable: false,
      renderCell: MenuItemCell,
    },
    {
      field: "path",
      headerName: t("path"),
      flex: 15,
      display: "flex",
      sortable: false,
      renderCell: PathCell,
    },
    {
      field: "permissions",
      headerName: t("permissions"),
      flex: 20,
      display: "flex",
      sortable: false,
      renderCell: PermissionsCell,
    },
    {
      field: "featureFlag",
      headerName: t("featureFlag"),
      flex: 12,
      display: "flex",
      sortable: false,
      renderCell: FeatureFlagCell,
    },
    {
      field: "users",
      headerName: t("users"),
      flex: 8,
      display: "flex",
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: UserCountCell,
    },
    {
      field: "userNames",
      headerName: t("userNames"),
      flex: 25,
      display: "flex",
      sortable: false,
      renderCell: UserNamesCell,
    },
  ];

  return (
    <HotkeyCompliantDataGrid<MenuItemStat>
      rows={menuItemStats}
      columns={columns}
      getRowId={(row) =>
        `${row.isChild ? "child" : "parent"}:${row.parentPath ?? ""}:${row.menuItem.path}`
      }
      getRowHeight={() => "auto"}
      onRowClick={({ row }: GridRowParams<MenuItemStat>) => {
        onMenuItemClick(row.menuItem, row.users);
      }}
      getRowClassName={({ row }: GridRowClassNameParams<MenuItemStat>) =>
        row.isChild ? "permissions-row-child" : ""
      }
      disableColumnReorder
      sx={{
        minHeight: 200,
        "& .MuiDataGrid-row": {
          cursor: "pointer",
        },
        "& .permissions-row-child": {
          backgroundColor: "action.selected",
          opacity: 0.9,
        },
      }}
    />
  );
};
